import { ByzzerTable } from '@/components/ByzzerTable';
import { DodFactFilterType, DodFilterType, DodProductFilterTypes, RowColConfig } from '@/components/DodConfigEditor/types';
import { useApp, useUser } from '@/contexts/UserContext';
import {
    ByzzerButton,
    ByzzerChangeEventHandler,
    ByzzerCheckableChangeEvent,
    ByzzerCheckbox,
    ByzzerTipIcon,
} from '@byzzer/ui-components';
import { CellClassParams, ColDef, GridOptions, CellClickedEvent, GridReadyEvent, HeaderClassParams, IHeaderGroupParams, IHeaderParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import classNames from 'classnames';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import './DodFactValuePickerTable.scss';
import { DodFactSet, DodFilters } from '@/types/DodRun';
import { ByzzerLink } from '@/components/form';
import { cloneDeep, debounce, intersection } from "lodash";
import useStateRef from "react-usestateref";
import { TippyProps } from "@tippyjs/react";
import { useCategoryService } from "@/services/category.service";
import {
    alertSelectionLimitExceed,
    filterValuesToStrings,
    isBrandFactStacked,
    isCategoryFactStacked,
    isCdiBdiFact,
    isProductSelectionsFactStacked,
    isSubCategoryFactStacked,
    isWeeksSellingFact,
    isWorkInProgressFactsDisabled,
} from '@/components/DodConfigEditor/common/utils';
import { ByzColDef, ByzColumnDef } from '@/types/TableTypes';
import { getRegexForSearchString } from '@/utils';
import { useDodWizard } from '@/components/DodConfigEditor/DodRunConfigWizard/DodWizardContext';
import { PlatformLink } from '@/types/ApiTypes';

export type DodFactValuePickerTableProps = {
    name?: string;
    className?: string;
    filterType: DodFilterType<DodFactFilterType>;
    shareFactGroup: ShareFactGroup;
    actions?: ActionConfig[];
    includeActions?: boolean;
    filters: DodFilters;
    value: DodFactSet[];
    filterText?: string;
    onApply?: ByzzerChangeEventHandler<DodFactSet[]>;
    limit?: number;
    factTriggerRef: any;
    acvArticleLinkData: PlatformLink | null;
};

export type ShareFactGroup = 'category' | 'subcategory' | 'brand' | 'product';

const baseClassName = 'dod-fact-value-picker-table';

type SelectAllHeaderProps = IHeaderParams & {
    isChecked: () => boolean;
    onSelectAllChange: (checked: boolean) => void;
};

const SelectAllHeader = ({ isChecked, onSelectAllChange }: SelectAllHeaderProps) => (
    <div className={`${baseClassName}__select-all-header`} onClick={(event) => event.stopPropagation()}>
        <ByzzerCheckbox
            checked={isChecked()}
            label="Select All"
            onChange={({ checked }) => onSelectAllChange(checked)}
        />
    </div>
);

const FactNameGroupHeader = ({ api, columnApi, columnGroup, displayName }: IHeaderGroupParams) => {
    const menuButtonRef = useRef<HTMLButtonElement>(null);
    const factNameColumn = columnGroup.getLeafColumns()[0];
    const [sort, setSort] = useState(factNameColumn.getSort());

    useEffect(() => {
        const handleSortChanged = () => setSort(factNameColumn.getSort());
        factNameColumn.addEventListener('sortChanged', handleSortChanged);

        return () => factNameColumn.removeEventListener('sortChanged', handleSortChanged);
    }, [factNameColumn]);

    const handleSort = (event: React.MouseEvent<HTMLButtonElement>) => {
        const nextSort = sort === 'asc' ? 'desc' : sort === 'desc' ? null : 'asc';
        columnApi.applyColumnState({
            state: [{ colId: factNameColumn.getColId(), sort: nextSort }],
            ...(!event.shiftKey && { defaultState: { sort: null } }),
        });
    };

    return (
        <div className={`${baseClassName}__fact-name-group-header`}>
            <button
                type="button"
                className={`${baseClassName}__fact-name-group-header-title`}
                onClick={handleSort}
            >
                <span>{displayName}</span>
                {sort && <span className={`ag-icon ag-icon-${sort}`} aria-hidden="true" />}
            </button>
            <button
                ref={menuButtonRef}
                type="button"
                className={`${baseClassName}__fact-name-group-header-menu`}
                aria-label={`Open ${displayName} column menu`}
                onClick={() => menuButtonRef.current && api.showColumnMenuAfterButtonClick(factNameColumn, menuButtonRef.current)}
            >
                <span className="ag-icon ag-icon-menu" aria-hidden="true" />
            </button>
        </div>
    );
};

const defaultTippyOptions: TippyProps = {
    appendTo: () => {
        return document.body;
    }
}

const checkForTooltipCondition = (displayName) => {
    if (['% ACV (Max)', '$ ACV Selling (Max)'].includes(displayName)) return true;
    if (
        displayName.includes('% $ Incremental') ||
        displayName.includes('% Units Incremental') ||
        displayName.includes('% $ Lift') ||
        displayName.includes('% Unit Lift')
    )
        return true;
    return false;
};

const shouldShowCostcoWarning = (displayText: string, dataItem: DodFactSet): boolean => {
    // BYZ-19710, BYZ-19730, BYZ-19748 As per this epic, we are releasing costco data for only a set of facts given in below list
    // any tdp/acv/upc/avg selling facts that are not in the approved list should trigger a warning, hence the not-in condition below
    const approvedFactsForCostcoData = [
        "Total TDP",
        "Total Velocity ($/TDP)",
        "Total Unit Velocity (Units/TDP)",
        "Total EQ Unit Velocity (EQ Units/TDP)",
        "Total Base $ Velocity (Base $/TDP)",
        "Total Base Unit Velocity (Base Units/TDP)",
        "Total Base EQ Unit Velocity (Base EQ Units/TDP)",
        "% ACV (Max)",
        "$ / %ACV (Max)",
        "Units / %ACV (Max)",
        "EQ Units / %ACV (Max)",
        "$ ACV (Max)",
        "$ / $MM ACV (Max)",
        "Units / $MM ACV (Max)",
        "EQ Units / $MM ACV (Max)",
        "Total ACV Selling (available for UPC-level only)",
        "Total Average Number of Stores Selling (available for UPC-level only)",
        "Store Weeks Selling (available for UPC-level only)",
        "Units per Store-Week Selling (available for UPC-level only)",
        "EQ Units per Store-Week Selling (available for UPC-level only)",
        "$ per Store-Week Selling (available for UPC-level only)",
        "% of Stores Selling (available for UPC-level only)"
    ].map(fact => fact.toLowerCase());
    // pls note: number of stores selling and number of store weeks selling are considered UPC-level only facts
    const isTdp = displayText.includes('tdp') && !approvedFactsForCostcoData.includes(displayText);
    const isAcv = displayText.includes('acv') && !displayText.includes('estimated market acv') && !approvedFactsForCostcoData.includes(displayText);
    const isAverageStoresSelling = displayText.includes('average number of stores selling') && !approvedFactsForCostcoData.includes(displayText);
    const isUpcRequired = dataItem.isUPCrequired && !approvedFactsForCostcoData.includes(displayText);

    return isTdp || isAcv || isAverageStoresSelling || isUpcRequired;
};

const tooltipForFact = (fact) => {
    let customFactName = fact;
    if (fact === '% ACV (Max)') {
        customFactName = '% ACV';
    } else if (fact === '$ ACV Selling (Max)') {
        customFactName = '$ ACV Selling';
    } else if (fact.includes('% $ Incremental') || fact.includes('% Units Incremental')) {
        customFactName = 'Incremental';
    } else if (fact.includes('% $ Lift') || fact.includes('% Unit Lift')) {
        customFactName = 'Lift';
    }

    // const factSell = fact === '% ACV (Max)' ? '% ACV' : '$ ACV Selling';
    switch (customFactName) {
        case '% ACV':
            return `At the UPC and week level, ${fact} is the same fact as ${customFactName}. ${fact} differs from ${customFactName} at any hierarchy level that is higher than UPC, because it is the maximum ${customFactName} for the items and weeks combination based on the selections.`;
        case '$ ACV Selling':
            return `At the UPC level, ${fact} is the same fact as ${customFactName}. ${fact} differs from ${customFactName} at any hierarchy level that is higher than UPC, because it is the maximum ${customFactName} out of any item based on the selections.`;
        case 'Incremental':
            return `${fact} is the incremental volume compared to the observed volume. Use this metric to understand what portion of your total sales were incremental. Formula = (Incremental Volume/Observed Volume)*100`;
        case 'Lift':
            return `${fact} is the incremental volume compared to the base volume. Use this metric to understand the % of sales that were gained over the base, or expected, volume. Formula = (Incremental Volume/Base Volume)*100`;
        default:
            break;
    }
};
const factToolTipForTDPAndACV = (dataItem: DodFactSet, acvArticleLinkData: PlatformLink | null) => {
    // todo: checkCategoryForDepartment to be added
    if ((dataItem.display.toLocaleLowerCase().includes('tdp') || dataItem.display.toLocaleLowerCase().includes('acv'))) {
        return (
            <ByzzerTipIcon
                interactive
                tipLocation={'top'}
                tippyProps={defaultTippyOptions}
                tip={
                    <div>
                        Distribution facts are calculated using Category Weighted Distribution for all Alcohol products.
                        Read our{' '}
                        <ByzzerLink
                            theme={'dark'}
                            // @ts-ignore - does 'rel' do anything here? throws type error...
                            rel="noreferrer"
                        >
                            <a
                                target="_blank"
                                href={acvArticleLinkData?.linkUrl ?? '#'}
                            >
                                {acvArticleLinkData?.linkText ?? 'ACV Article'}
                            </a>
                        </ByzzerLink>{' '}
                        to learn more about what this means.
                    </div>
                }
            />
        );
    }
    return null;
};

const factToolTipForCostcoTDPAndACV = (dataItem: DodFactSet) => {
    const displayText = dataItem.display.toLocaleLowerCase();
    if (shouldShowCostcoWarning(displayText, dataItem)) {
        return (
            <ByzzerTipIcon
                type={'warning'}
                interactive
                tipLocation={'top'}
                tippyProps={defaultTippyOptions}
                // BYZ-19710, BYZ-19730, BYZ-19748 As per this epic, we are releasing costco data for only a set of facts given in above list
                // any tdp/acv/upc/avg selling facts that are not in the approved list should trigger a warning
                // the warning message is now updated
                tip={<div>This fact is not reported for the Costco market due to current data restrictions.</div>}
            />
        );
    }
    return null;
};

const factToolTipForLaunchDate = (dataItem: DodFactSet) => {
    if (dataItem.display.toLocaleLowerCase() === 'launch date') {
        return (
            <ByzzerTipIcon
                interactive
                tipLocation={'top'}
                tippyProps={defaultTippyOptions}
                tip={<div>
                    Launch date is the first date a product is present in any market. Products that have been selling earlier than the Byzzer dataset will always show the Launch Date as the earliest date in the data.
                </div>}
            />
        );
    }
    return null;
};

const factToolTipForStackedField = (rowColConfigs: RowColConfig[], flag: string) => {
    let tip: string | React.ReactNode = '';
    if (isCategoryFactStacked(rowColConfigs, flag)) {
        tip = <div className={`${baseClassName}__disabled-tip`}>You cannot select these share facts because you have stacked Category. Unstack this field if you wish to add Share of Category facts.</div>
    }
    else if (isSubCategoryFactStacked(rowColConfigs, flag)) {
        tip = <div className={`${baseClassName}__disabled-tip`}>You cannot select these share facts because you have stacked Subcategory. Unstack this field if you wish to add Share of Subcategory facts.</div>;
    }
    else if (isBrandFactStacked(rowColConfigs, flag)) {
        tip = <div className={`${baseClassName}__disabled-tip`}>You cannot select these share facts because you have stacked Brand. Unstack this field if you wish to add Share of Brand facts.</div>
    }
    else if (isProductSelectionsFactStacked(rowColConfigs, flag)) {
        tip = <div className={`${baseClassName}__disabled-tip`}>You cannot select these share facts because you have stacked product(s). Unstack these field(s) if you wish to add Share of Product Selections facts.</div>;
    }
    return tip;
}

const checkboxColumnWidth = 65;
const shareBasePeriodColumnWidth = 50;
const sharePointChangeColumnWidth = 80;
const flagToFactMapping: Record<string, string> = {
    cp: 'fact',
    ya: 'yearAgo',
    cvya: 'changeVsYearAgo',
    pcvya: 'percentChangeVsYearAgo',
    c: 'category',
    cya: 'categoryYearAgo',
    ctya: 'categoryTwoYearAgo',
    ctya3: 'categoryThreeYearAgo',
    cfya: 'categoryFourYearAgo',
    cspcya: 'categorySharePointChangeYearAgo',
    cspctya: 'categorySharePointChangeTwoYearAgo',
    cspctya3: 'categorySharePointChangeThreeYearAgo',
    cspcfya: 'categorySharePointChangeFourYearAgo',
    b: 'brand',
    bya: 'brandYearAgo',
    btya: 'brandTwoYearAgo',
    btya3: 'brandThreeYearAgo',
    bfya: 'brandFourYearAgo',
    bspcya: 'brandSharePointChangeYearAgo',
    bspctya: 'brandSharePointChangeTwoYearAgo',
    bspctya3: 'brandSharePointChangeThreeYearAgo',
    bspcfya: 'brandSharePointChangeFourYearAgo',
    ps: 'productSelections',
    psya: 'productSelectionsYearAgo',
    pstya: 'productSelectionsTwoYearAgo',
    pstya3: 'productSelectionsThreeYearAgo',
    psfya: 'productSelectionsFourYearAgo',
    psspcya: 'productSelectionsSharePointChangeYearAgo',
    psspctya: 'productSelectionsSharePointChangeTwoYearAgo',
    psspctya3: 'productSelectionsSharePointChangeThreeYearAgo',
    psspcfya: 'productSelectionsSharePointChangeFourYearAgo',
    tya: 'twoYearAgo',
    tya3: 'threeYearAgo',
    fya: 'fourYearAgo',
    sc: 'subCategory',
    scya: 'subCategoryYearAgo',
    sctya: 'subCategoryTwoYearAgo',
    sctya3: 'subCategoryThreeYearAgo',
    scfya: 'subCategoryFourYearAgo',
    scspcya: 'subCategorySharePointChangeYearAgo',
    scspctya: 'subCategorySharePointChangeTwoYearAgo',
    scspctya3: 'subCategorySharePointChangeThreeYearAgo',
    scspcfya: 'subCategorySharePointChangeFourYearAgo',
    cvtya: 'changeVsTwoYearAgo',
    pcvtya: 'percentChangeVsTwoYearAgo',
    cvtya3: 'changeVsThreeYearAgo',
    pcvtya3: 'percentChangeVsThreeYearAgo',
    cvfya: 'changeVsFourYearAgo',
    pcvfya: 'percentChangeVsFourYearAgo',
}

const flagToAllowedIndexMapping: Record<string, number> = {
    cp: 1,
    ya: 2,
    cvya: 4,
    pcvya: 11,
    c: 5,
    cya: 6,
    b: 7,
    bya: 8,
    ps: 9,
    psya: 10,
    tya: 3,
    tya3: 14,
    fya: 15,
    sc: 12,
    scya: 13,
    cvtya: 16,
    pcvtya: 19,
    cvtya3: 17,
    pcvtya3: 20,
    cvfya: 18,
    pcvfya: 21,
    ctya: 22,
    ctya3: 23,
    cfya: 24,
    btya: 25,
    btya3: 26,
    bfya: 27,
    pstya: 28,
    pstya3: 29,
    psfya: 30,
    sctya: 31,
    sctya3: 32,
    scfya: 33,
    cspcya: 34,
    cspctya: 35,
    cspctya3: 36,
    cspcfya: 37,
    bspcya: 38,
    bspctya: 39,
    bspctya3: 40,
    bspcfya: 41,
    psspcya: 42,
    psspctya: 43,
    psspctya3: 44,
    psspcfya: 45,
    scspcya: 46,
    scspctya: 47,
    scspctya3: 48,
    scspcfya: 49
}

const coreFlags: string[] = [
    'cp',
    'ya',
    'cvya',
    'pcvya',
    'tya',
    'tya3',
    'fya',
    'cvtya',
    'pcvtya',
    'cvtya3',
    'pcvtya3',
    'cvfya',
    'pcvfya'
];
const shareFlags: string[] = [
    'c',
    'cya',
    'ctya',
    'ctya3',
    'cfya',
    'cspcya',
    'cspctya',
    'cspctya3',
    'cspcfya',
    'b',
    'bya',
    'btya',
    'btya3',
    'bfya',
    'bspcya',
    'bspctya',
    'bspctya3',
    'bspcfya',
    'ps',
    'psya',
    'pstya',
    'pstya3',
    'psfya',
    'psspcya',
    'psspctya',
    'psspctya3',
    'psspcfya',
    'sc',
    'scya',
    'sctya',
    'sctya3',
    'scfya',
    'scspcya',
    'scspctya',
    'scspctya3',
    'scspcfya',
];

const shareFlagsByGroup: Record<ShareFactGroup, string[]> = {
    category: shareFlags.slice(0, 9),
    brand: shareFlags.slice(9, 18),
    product: shareFlags.slice(18, 27),
    subcategory: shareFlags.slice(27, 36),
};

const shareGroupIds: Record<ShareFactGroup, string[]> = {
    category: ['shareFactName', 'categoryBasePeriods', 'categorySharePointChange'],
    subcategory: ['shareFactName', 'subCategoryBasePeriods', 'subCategorySharePointChange'],
    brand: ['shareFactName', 'brandBasePeriods', 'brandSharePointChange'],
    product: ['shareFactName', 'productSelectionsBasePeriods', 'productSelectionsSharePointChange'],
};

const checkboxGridOptions: GridOptions = {
    rowHeight: 24
}
const defaultColDef = {
    wrapText: true,
    autoHeight: true,
    wrapHeaderText: true,
    autoHeaderHeight: true
};

type FlagAndIndex = {
    flag: string;
    index: number;
}

function getFactLeafColumnDefs(
    columns: ByzColumnDef<DodFactSet, DodFactValuePickerTableColId>[]
): ByzColDef<DodFactSet, DodFactValuePickerTableColId>[] {
    return columns.flatMap((column) => 'children' in column ? getFactLeafColumnDefs(column.children) : column);
}

type DodFactValuePickerTableColId =
    'subCategoryFourYearAgoCheckbox' |
    'coreDisplay' |
    'shareDisplay' |
    'currentPeriodCheckbox' |
    'yearAgoCheckbox' |
    'changeVsYearAgoCheckbox' |
    'percentChangeVsYearAgoCheckbox' |
    'categoryCheckbox' |
    'categoryYearAgoCheckbox' |
    'categoryTwoYearAgoCheckbox' |
    'categoryThreeYearAgoCheckbox' |
    'categoryFourYearAgoCheckbox' |
    'categorySharePointChangeYearAgoCheckbox' |
    'categorySharePointChangeTwoYearAgoCheckbox' |
    'categorySharePointChangeThreeYearAgoCheckbox' |
    'categorySharePointChangeFourYearAgoCheckbox' |
    'brandCheckbox' |
    'brandYearAgoCheckbox' |
    'brandTwoYearAgoCheckbox' |
    'brandThreeYearAgoCheckbox' |
    'brandFourYearAgoCheckbox' |
    'brandSharePointChangeYearAgoCheckbox' |
    'brandSharePointChangeTwoYearAgoCheckbox' |
    'brandSharePointChangeThreeYearAgoCheckbox' |
    'brandSharePointChangeFourYearAgoCheckbox' |
    'productSelectionsCheckbox' |
    'productSelectionsYearAgoCheckbox' |
    'productSelectionsTwoYearAgoCheckbox' |
    'productSelectionsThreeYearAgoCheckbox' |
    'productSelectionsFourYearAgoCheckbox' |
    'productSelectionsSharePointChangeYearAgoCheckbox' |
    'productSelectionsSharePointChangeTwoYearAgoCheckbox' |
    'productSelectionsSharePointChangeThreeYearAgoCheckbox' |
    'productSelectionsSharePointChangeFourYearAgoCheckbox' |
    'subCategoryCheckbox' |
    'subCategoryYearAgoCheckbox' |
    'subCategoryTwoYearAgoCheckbox' |
    'subCategoryThreeYearAgoCheckbox' |
    'subCategorFourYearAgoCheckbox' |
    'subCategorySharePointChangeYearAgoCheckbox' |
    'subCategorySharePointChangeTwoYearAgoCheckbox' |
    'subCategorySharePointChangeThreeYearAgoCheckbox' |
    'subCategorySharePointChangeFourYearAgoCheckbox' |
    'twoYearAgoCheckbox' |
    'threeYearAgoCheckbox' |
    'fourYearAgoCheckbox' |
    'changeVsTwoYearAgoCheckbox' |
    'percentChangeVsTwoYearAgoCheckbox' |
    'changeVsThreeYearAgoCheckbox' |
    'percentChangeVsThreeYearAgoCheckbox' |
    'changeVsFourYearAgoCheckbox' |
    'percentChangeVsFourYearAgoCheckbox';

// In dod, weeks selling and cdi/bdi facts are in work in progress. These are its facts ids.
// Fact ids remain the same across environments, hence we are using the ids to disable summed product,
// summed market, subtotal and hide category features in dod for these fact selections
// export const weeksSellingFactIds: number[] = [1031];
// export const cdiBdiFactIds: number[] = [1021, 1067, 1068, 1069, 1071, 1072, 1073, 1074, 1075];
const inProgressFactsTooltipLink = 'https://learn.byzzer.ai/en/articles/10542218-number-of-weeks-selling-fact-restrictions';

const DodFactValuePickerTable = ({
    filterType,
    shareFactGroup,
    actions,
    includeActions,
    className,
    name,
    onApply,
    filters,
    filterText = '',
    value,
    limit = Infinity,
    factTriggerRef,
    acvArticleLinkData,
    ...props
}: DodFactValuePickerTableProps) => {
    const gridRef = useRef<AgGridReact>(null);
    const { factConfig, categories: companyCategories, features } = useApp();
    const { getCategoriesForDepartments, getAllCategoriesByDepartmentsAndSuperCategories } = useCategoryService();
    const [selections, setSelections, selectionsRef] = useStateRef<Record<string, boolean>>({});
    const [currentFacts, setCurrentFacts, currentFactsRef] = useStateRef<any[]>([]);
    const [matchingFacts, setMatchingFacts, matchingFactsRef] = useStateRef<DodFactSet[]>([]);
    const lastFlagAndIndex = useRef<FlagAndIndex | null>(null);
    const shiftActive = useRef<boolean>(false);
    // Snapshot of selections when anchor (first non-shift click) is set
    const shiftBaselineRef = useRef<Record<string, boolean> | null>(null);
    // Track keys that were applied by the most recent shift operation
    const lastAppliedKeysRef = useRef<Set<string>>(new Set());
    const shareColumnVisibilityRef = useRef<Partial<Record<ShareFactGroup, Record<string, boolean>>>>({});
    // Batched redraw system
    const pendingRowsToRedraw = useRef<Set<any>>(new Set());
    const hasPendingFullRedraw = useRef<boolean>(false);
    const isUPCSelected = Boolean(filters?.upcs.values.length || filters?.upcs.summedSelections.length);
    const isBrandSelected = Boolean(filters?.brands.values.length || filters?.brands.summedSelections.length);
    const isSubCategorySelected = Boolean(filters?.subcategories.values.length || filters?.subcategories.summedSelections.length)
    const { runConfig, rowColConfigs } = useDodWizard();

    useEffect(() => {
        const onKeyUp = (ev: KeyboardEvent) => {
            if (ev.key === 'Shift') {
                // Clear shift mode when Shift is released so next single click behaves normally
                shiftActive.current = false;
                // keep anchor, but drop baseline so subsequent non-shift clicks will snapshot again
                shiftBaselineRef.current = null;
                // clear tracking of last applied keys
                lastAppliedKeysRef.current = new Set();
            }
        };
        window.addEventListener('keyup', onKeyUp);
        return () => window.removeEventListener('keyup', onKeyUp);
    }, []);

    useEffect(() => {
        lastFlagAndIndex.current = null;
        shiftActive.current = false;
        shiftBaselineRef.current = null;
        lastAppliedKeysRef.current = new Set();
    }, [shareFactGroup]);

    const extendedDataYears = useMemo(() => {
        return features?.extendedDataYears === 5;
    }, [features]);

    // disable a fact if is a upc level fact and requires upc in currect user selection
    const isUpcDisabledFact = (fact: DodFactSet) => {
        return fact?.isUPCrequired && !isUPCSelected;
    };

    // to check if summed product is present in user selections
    const isSummedProductSelected = () => {
        let isSummedProductSelected: boolean = false;
        const productFilters = Object.keys(filters).filter(
            (filter) => filter !== 'markets' && filter !== 'timePeriods'
        );
        for (const filterType of productFilters) {
            if (
                [
                    DodProductFilterTypes.CHARACTERISTICS,
                    DodProductFilterTypes.CUSTOM_CHARACTERISTICS,
                    DodProductFilterTypes.PPGS,
                ].includes(filterType as DodProductFilterTypes)
            ) {
                const filterKeys = Object.keys(filters[filterType]);
                for (const key of filterKeys) {
                    if (filters[filterType][key].summedSelections.length) {
                        isSummedProductSelected = true;
                    }
                }

            } else {
                if (filters[filterType].summedSelections.length) {
                    isSummedProductSelected = true;
                }
            }
        }
        return isSummedProductSelected;
    };

    const isFactDisabled = (dataItem: DodFactSet) => {
        return (
            isUpcDisabledFact(dataItem) || isWorkInProgressFactsDisabled({ fact: dataItem, runConfig, rowColConfigs })
        );
    };

    const getDisabledTip = (fact: DodFactSet, extendedDataYears?: boolean) => {
        let tip: string | React.ReactNode = '';
        if (extendedDataYears) {
            tip = <div className={`${baseClassName}__disabled-tip`}>You license the standard 3 years of data. If you wish to upgrade to 5 years of data, please contact your account rep.</div>
        }
        else if (isCdiBdiFact(fact)) {
            tip = <div className={`${baseClassName}__disabled-tip`}>CDI/BDI facts are not compatible with all selection types yet. Please review this <b><u><a className={`${baseClassName}__disabled-tip`} href={inProgressFactsTooltipLink} target="_blank" rel="noreferrer">article</a></u></b> to understand which of your selections are not compatible.</div>
        }
        else if (isWeeksSellingFact(fact)) {
            tip = <div className={`${baseClassName}__disabled-tip`}>Number of Weeks Selling facts are not compatible with all selection types yet. Please review this <b><u><a className={`${baseClassName}__disabled-tip`} href={inProgressFactsTooltipLink} target="_blank" rel="noreferrer">article</a></u></b> to understand which of your selections are not compatible.</div>
        }
        return tip;
    };

    const { categories: userCategories, subscription } = useUser();

    const isFreeUser = subscription?.metadata?.isFree ?? false;

    const includesAlcoholCategories = useMemo<boolean>(() => {

        const alcoholCategories = getCategoriesForDepartments(['ALCOHOL']);
        let categories = filters.categories.values as string[];

        if (
            filters.categories.values === 'all' ||
            filters.categories.summedSelections.some((ss) => ss.values === 'all')
        ) {
            categories = getAllCategoriesByDepartmentsAndSuperCategories(filters.departments, filters.superCategories);
        } else {
            categories = filterValuesToStrings(filters.categories, userCategories);
        }
        return Boolean(intersection(alcoholCategories, categories).length);
    }, [filters]);

    const includesCostcoMarket = useMemo<boolean>(() => {
        const isCostcoMarket = item => item.masterCompany === 'Costco';

        const hasCostcoInMarkets = filters.markets.values.some(isCostcoMarket);
        const hasCostcoInSummedSelections = filters.markets.summedSelections.some(
            (selection) => selection.values.some(isCostcoMarket)
        );

        return hasCostcoInMarkets || hasCostcoInSummedSelections;
    }, [filters]);


    const coreFactsColumnDefs: ByzColDef<DodFactSet, DodFactValuePickerTableColId>[] = useMemo(() => [{
        headerName: 'Fact Name',
        colId: 'coreDisplay',
        field: 'display',
        minWidth: 250,
        flex: 1,
        pinned: 'left',
        lockPinned: true,
        sortable: true,
        filter: false,
        headerClass: `${baseClassName}__fact-name-header`,
        cellClass: `${baseClassName}__fact-name-cell`,
        cellRenderer: ({ data }) => (

            <div className={classNames(`${baseClassName}__fact-name`, {
                [`${baseClassName}__fact-name--disabled`]: isFactDisabled(data),
            })}>
                <h1>{data?.display}</h1>
                <div className={`${baseClassName}__info-icons`}>
                    {includesAlcoholCategories && factToolTipForTDPAndACV(data, acvArticleLinkData)}
                    {includesCostcoMarket && factToolTipForCostcoTDPAndACV(data)}
                    {factToolTipForLaunchDate(data)}
                </div>
            </div>
        ),
        tooltipValueGetter: ({ data }) => {
            return checkForTooltipCondition(data?.display) ? tooltipForFact(data?.display) : `${data?.display}`;
        },
        disableHide: true
    }, {
        colId: 'currentPeriodCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Current Period',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'cp'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data?.id}:${flag}`;
            if (!data?.allowedFacts?.includes(1)) return null;
            return (
                // TODO: To remove the commented ByzzerMessagePopUp components post BYZ-15610 testing
                // <ByzzerMessagePopUp
                //     tipTrigger='hover'
                //     tip={getDisabledTip(data)}
                //     interactive={true}
                //     disabled={!isFactDisabled(data)}
                // >
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={Boolean(value) && Boolean(context) ? context?.[value] : undefined}
                    disabled={isFactDisabled(data)}
                    disabledTip={getDisabledTip(data)}
                    tipDelay={10}
                />
                // </ByzzerMessagePopUp>
            );
        },
    }, {
        colId: 'yearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'ya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            if (!data.allowedFacts?.includes(2)) return null;

            return (
                // <ByzzerMessagePopUp
                //     tipTrigger='hover'
                //     tip={getDisabledTip(data)}
                //     interactive={true}
                //     disabled={!isFactDisabled(data)}
                // >
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data)}
                    disabledTip={getDisabledTip(data)}
                    tipDelay={10}
                />
                // </ByzzerMessagePopUp>
            );
        },
    }, {
        colId: 'changeVsYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Change vs Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'cvya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            if (!data.allowedFacts?.includes(11)) return null;

            return (
                // <ByzzerMessagePopUp
                //     tipTrigger='hover'
                //     tip={getDisabledTip(data)}
                //     interactive={true}
                //     disabled={!isFactDisabled(data)}
                // >
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data)}
                    disabledTip={getDisabledTip(data)}
                    tipDelay={10}
                />
                // </ByzzerMessagePopUp>
            );
        },
    }, {
        colId: 'percentChangeVsYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: '% Change vs Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'pcvya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            if (!data.allowedFacts?.includes(4)) return null;

            return (
                // <ByzzerMessagePopUp
                //     tipTrigger='hover'
                //     tip={getDisabledTip(data)}
                //     interactive={true}
                //     disabled={!isFactDisabled(data)}
                // >
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data)}
                    disabledTip={getDisabledTip(data)}
                />
                // </ByzzerMessagePopUp>
            );
        },
    },
    {
        colId: 'twoYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: '2 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'tya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            if (!data.allowedFacts?.includes(3)) return null;

            return (
                // <ByzzerMessagePopUp
                //     tipTrigger='hover'
                //     tip={getDisabledTip(data)}
                //     interactive={true}
                //     disabled={!isFactDisabled(data)}
                // >
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data)}
                    disabledTip={getDisabledTip(data)}
                    tipDelay={10}
                />
                // </ByzzerMessagePopUp>
            );
        },
    },
    {
        colId: 'changeVsTwoYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Change vs 2 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'cvtya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            if (!data.allowedFacts?.includes(16)) return null;

            return (
                // <ByzzerMessagePopUp
                //     tipTrigger='hover'
                //     tip={getDisabledTip(data)}
                //     interactive={true}
                //     disabled={!isFactDisabled(data)}
                // >
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data)}
                    disabledTip={getDisabledTip(data)}
                    tipDelay={10}
                />
                // </ByzzerMessagePopUp>
            );
        },
    },
    {
        colId: 'percentChangeVsTwoYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: '% Change vs 2 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'pcvtya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            if (!data.allowedFacts?.includes(19)) return null;

            return (
                // <ByzzerMessagePopUp
                //     tipTrigger='hover'
                //     tip={getDisabledTip(data)}
                //     interactive={true}
                //     disabled={!isFactDisabled(data)}
                // >
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data)}
                    disabledTip={getDisabledTip(data)}
                    tipDelay={10}
                />
                // </ByzzerMessagePopUp>
            );
        },
    },
    {
        colId: 'threeYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: '3 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        hide: !extendedDataYears,
        cellRendererParams: {
            flag: 'tya3'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            if (!data.allowedFacts?.includes(14)) return null;

            return (
                // <ByzzerMessagePopUp
                //     tipTrigger='hover'
                //     tip={getDisabledTip(data, !extendedDataYears)}
                //     interactive={true}
                //     disabled={isFactDisabled(data) || !extendedDataYears}
                // >
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data) || !extendedDataYears}
                    disabledTip={getDisabledTip(data, !extendedDataYears)}
                    tipDelay={10}
                />
                // </ByzzerMessagePopUp>
            );
        },
    },
    {
        colId: 'changeVsThreeYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Change vs 3 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        hide: !extendedDataYears,
        cellRendererParams: {
            flag: 'cvtya3'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            if (!data.allowedFacts?.includes(17)) return null;
            return (
                // <ByzzerMessagePopUp
                //     tipTrigger='hover'
                //     tip={getDisabledTip(data, !extendedDataYears)}
                //     interactive={true}
                //     disabled={isFactDisabled(data) || !extendedDataYears}
                // >
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data) || !extendedDataYears}
                    disabledTip={getDisabledTip(data, !extendedDataYears)}
                    tipDelay={10}
                />
                // </ByzzerMessagePopUp>
            );
        },
    },
    {
        colId: 'percentChangeVsThreeYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: '% Change vs 3 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        hide: !extendedDataYears,
        cellRendererParams: {
            flag: 'pcvtya3'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            if (!data.allowedFacts?.includes(20)) return null;

            return (
                // <ByzzerMessagePopUp
                //     tipTrigger='hover'
                //     tip={getDisabledTip(data, !extendedDataYears)}
                //     interactive={true}
                //     disabled={isFactDisabled(data) || !extendedDataYears}
                // >
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data) || !extendedDataYears}
                    disabledTip={getDisabledTip(data, !extendedDataYears)}
                    tipDelay={10}
                />
                // </ByzzerMessagePopUp>
            );
        },
    },
    {
        colId: 'fourYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: '4 Year-Ago',
        hide: !extendedDataYears,
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'fya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            if (!data.allowedFacts?.includes(15)) return null;

            return (
                // <ByzzerMessagePopUp
                //     tipTrigger='hover'
                //     tip={getDisabledTip(data, !extendedDataYears)}
                //     interactive={true}
                //     disabled={isFactDisabled(data) || !extendedDataYears}
                // >
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data) || !extendedDataYears}
                    disabledTip={getDisabledTip(data, !extendedDataYears)}
                    tipDelay={10}
                />
                // </ByzzerMessagePopUp>
            );
        },
    },
    {
        colId: 'changeVsFourYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'Change vs 4 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        hide: !extendedDataYears,
        cellRendererParams: {
            flag: 'cvfya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            if (!data.allowedFacts?.includes(18)) return null;

            return (
                // <ByzzerMessagePopUp
                //     tipTrigger='hover'
                //     tip={getDisabledTip(data, !extendedDataYears)}
                //     interactive={true}
                //     disabled={isFactDisabled(data) || !extendedDataYears}
                // >
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data) || !extendedDataYears}
                    disabledTip={getDisabledTip(data, !extendedDataYears)}
                    tipDelay={10}
                />
                // </ByzzerMessagePopUp>
            );
        },
    },
    {
        colId: 'percentChangeVsFourYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: '% Change vs 4 Year-Ago',
        cellClass: `${baseClassName}__checkbox-cell`,
        hide: !extendedDataYears,
        cellRendererParams: {
            flag: 'pcvfya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            if (!data.allowedFacts?.includes(21)) return null;

            return (
                // <ByzzerMessagePopUp
                //     tipTrigger='hover'
                //     tip={getDisabledTip(data, !extendedDataYears)}
                //     interactive={true}
                //     disabled={isFactDisabled(data) || !extendedDataYears}
                // >
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data) || !extendedDataYears}
                    disabledTip={getDisabledTip(data, !extendedDataYears)}
                    tipDelay={10}
                />
                // </ByzzerMessagePopUp>
            );
        },
    }
    ], []);
    const shareFactsColumnDefs: ByzColumnDef<DodFactSet, DodFactValuePickerTableColId>[] = useMemo(() => {
        const columns: ByzColumnDef<DodFactSet, DodFactValuePickerTableColId>[] = [{
        headerName: 'Fact Name',
        groupId: 'shareFactName',
        marryChildren: true,
        headerGroupComponent: FactNameGroupHeader,
        children: [{
        headerName: '',
        colId: 'shareDisplay',
        field: 'display',
        minWidth: 250,
        width: 250,
        pinned: 'left',
        sortable: true,
        lockPinned: true,
        filter: true,
        headerClass: `${baseClassName}__fact-name-header`,
        headerComponent: SelectAllHeader,
        headerComponentParams: {
            isChecked: isSelectAllChecked,
            onSelectAllChange: handleSelectAllChange,
        },
        cellClass: `${baseClassName}__fact-name-cell`,
        cellRendererParams: {
            flag: ''
        },
        disableHide: true,
        cellRenderer: ({ data }) => (
            <div className={classNames(`${baseClassName}__fact-name`, {
                [`${baseClassName}__fact-name--disabled`]: isFactDisabled(data),
            })}>
                <h1>{data?.display}</h1>
                <div className={`${baseClassName}__info-icons`}>
                    {includesAlcoholCategories && factToolTipForTDPAndACV(data, acvArticleLinkData)}
                    {includesCostcoMarket && factToolTipForCostcoTDPAndACV(data)}
                </div>
            </div>
        ),
        tooltipValueGetter: ({ data }) => {
            return checkForTooltipCondition(data?.display) ? tooltipForFact(data?.display) : `${data?.display}`;
        },
    }],
    }, {
        headerName: 'Share of Category - Base Periods',
        groupId: 'categoryBasePeriods',
        marryChildren: true,
        children: [{
        colId: 'categoryCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'CY',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'c'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(5)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data) || isCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                />
            );
        },
    }, {
        colId: 'categoryYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'cya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(6)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data) || isCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                />
            );
        },
    },
    {
        colId: 'categoryTwoYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: '2 YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'ctya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(22)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data) || isCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                />
            );
        },
    },
    {
        colId: 'categoryThreeYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: '3 YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'ctya3'
        },
        hide: !extendedDataYears,
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data, !extendedDataYears);
            if (!data.allowedFacts?.includes(23)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data) || !extendedDataYears || isCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                    tipDelay={10}
                />
            );
        },
    },
    {
        colId: 'categoryFourYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: '4 YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'cfya'
        },
        hide: !extendedDataYears,
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data, !extendedDataYears);
            if (!data.allowedFacts?.includes(24)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data) || !extendedDataYears || isCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                    tipDelay={10}
                />
            );
        },
    }],
    }, {
        headerName: 'Share of Category - Share Point Change',
        groupId: 'categorySharePointChange',
        marryChildren: true,
        children: [{
        colId: 'categorySharePointChangeYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'SPC vs. YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'cspcya'
        },
        cellRenderer: ({ data, flag, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(34)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data) || isCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                />
            );
        },
    },
    {
        colId: 'categorySharePointChangeTwoYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'SPC vs. 2 YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'cspctya'
        },
        cellRenderer: ({ data, flag, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(35)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data) || isCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                />
            );
        },
    },
    {
        colId: 'categorySharePointChangeThreeYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'SPC vs. 3 YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'cspctya3'
        },
        hide: !extendedDataYears,
        cellRenderer: ({ data, flag, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data, !extendedDataYears);
            if (!data.allowedFacts?.includes(36)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data) || !extendedDataYears || isCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                    tipDelay={10}
                />
            );
        },
    },
    {
        colId: 'categorySharePointChangeFourYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'SPC vs. 4 YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'cspcfya'
        },
        hide: !extendedDataYears,
        cellRenderer: ({ data, flag, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data, !extendedDataYears);
            if (!data.allowedFacts?.includes(37)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={isFactDisabled(data) || !extendedDataYears || isCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                    tipDelay={10}
                />
            );
        },
    }],
    }, {
        headerName: 'Share of Subcategory - Base Periods',
        groupId: 'subCategoryBasePeriods',
        marryChildren: true,
        children: [{
        colId: 'subCategoryCheckbox',
        minWidth: 85,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'CY',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'sc'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(12)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={!isSubCategorySelected || isSubCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                />
            );
        },
    },
    {
        colId: 'subCategoryYearAgoCheckbox',
        minWidth: 85,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'scya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(13)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={!isSubCategorySelected || isSubCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                />
            );
        },
    },
    {
        colId: 'subCategoryTwoYearAgoCheckbox',
        minWidth: 85,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: '2 YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'sctya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(31)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={!isSubCategorySelected || isSubCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                />
            );
        },
    },
    {
        colId: 'subCategoryThreeYearAgoCheckbox',
        minWidth: 85,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: '3 YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'sctya3'
        },
        hide: !extendedDataYears,
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data, !extendedDataYears);
            if (!data.allowedFacts?.includes(32)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={!isSubCategorySelected || !extendedDataYears || isSubCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                    tipDelay={10}
                />
            );
        },
    },
    {
        colId: 'subCategoryFourYearAgoCheckbox',
        minWidth: 85,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: '4 YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'scfya'
        },
        hide: !extendedDataYears,
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data, !extendedDataYears);
            if (!data.allowedFacts?.includes(33)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={!isSubCategorySelected || !extendedDataYears || isSubCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                    tipDelay={10}
                />
            );
        },
    }],
    }, {
        headerName: 'Share of Subcategory - Share Point Change',
        groupId: 'subCategorySharePointChange',
        marryChildren: true,
        children: [{
        colId: 'subCategorySharePointChangeYearAgoCheckbox',
        minWidth: 85,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'SPC vs. YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'scspcya'
        },
        cellRenderer: ({ data, flag, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(46)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={!isSubCategorySelected || isSubCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                />
            );
        },
    },
    {
        colId: 'subCategorySharePointChangeTwoYearAgoCheckbox',
        minWidth: 85,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'SPC vs. 2 YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'scspctya'
        },
        cellRenderer: ({ data, flag, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(47)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={!isSubCategorySelected || isSubCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                />
            );
        },
    },
    {
        colId: 'subCategorySharePointChangeThreeYearAgoCheckbox',
        minWidth: 85,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'SPC vs. 3 YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'scspctya3'
        },
        hide: !extendedDataYears,
        cellRenderer: ({ data, flag, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data, !extendedDataYears);
            if (!data.allowedFacts?.includes(48)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={!isSubCategorySelected || !extendedDataYears || isSubCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                    tipDelay={10}
                />
            );
        },
    },
    {
        colId: 'subCategorySharePointChangeFourYearAgoCheckbox',
        minWidth: 85,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'SPC vs. 4 YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'scspcfya'
        },
        hide: !extendedDataYears,
        cellRenderer: ({ data, flag, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data, !extendedDataYears);
            if (!data.allowedFacts?.includes(49)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={!isSubCategorySelected || !extendedDataYears || isSubCategoryFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                    tipDelay={10}
                />
            );
        },
    }],
    }, {
        headerName: 'Share of Brand - Base Periods',
        groupId: 'brandBasePeriods',
        marryChildren: true,
        children: [{
        colId: 'brandCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'CY',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'b'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(7)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={!isUPCSelected && !isBrandSelected || isBrandFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                />
            );
        },
    }, {
        colId: 'brandYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'bya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(8)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={!isUPCSelected && !isBrandSelected || isBrandFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                />
            );
        },
    },
    {
        colId: 'brandTwoYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: '2 YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'btya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(25)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={(!isUPCSelected && !isBrandSelected) || isBrandFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                />
            );
        },
    }, {
        colId: 'brandThreeYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: '3 YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'btya3'
        },
        hide: !extendedDataYears,
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data, !extendedDataYears);
            if (!data.allowedFacts?.includes(26)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={
                        (!isUPCSelected && !isBrandSelected) ||
                        !extendedDataYears ||
                        isBrandFactStacked(rowColConfigs, flag)
                    }
                    disabledTip={disabledTip}
                    tipDelay={10}
                />
            );
        },
    }, {
        colId: 'brandFourYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: '4 YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'bfya'
        },
        hide: !extendedDataYears,
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data, !extendedDataYears);
            if (!data.allowedFacts?.includes(27)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={
                        (!isUPCSelected && !isBrandSelected) ||
                        !extendedDataYears ||
                        isBrandFactStacked(rowColConfigs, flag)
                    }
                    disabledTip={disabledTip}
                    tipDelay={10}
                />
            );
        },
    }],
    }, {
        headerName: 'Share of Brand - Share Point Change',
        groupId: 'brandSharePointChange',
        marryChildren: true,
        children: [{
        colId: 'brandSharePointChangeYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'SPC vs. YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'bspcya'
        },
        cellRenderer: ({ data, flag, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(38)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={!isUPCSelected && !isBrandSelected || isBrandFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                />
            );
        },
    },
    {
        colId: 'brandSharePointChangeTwoYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'SPC vs. 2 YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'bspctya'
        },
        cellRenderer: ({ data, flag, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(39)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={(!isUPCSelected && !isBrandSelected) || isBrandFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                />
            );
        },
    }, {
        colId: 'brandSharePointChangeThreeYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'SPC vs. 3 YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'bspctya3'
        },
        hide: !extendedDataYears,
        cellRenderer: ({ data, flag, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data, !extendedDataYears);
            if (!data.allowedFacts?.includes(40)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={(!isUPCSelected && !isBrandSelected) || !extendedDataYears || isBrandFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                    tipDelay={10}
                />
            );
        },
    }, {
        colId: 'brandSharePointChangeFourYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'SPC vs. 4 YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'bspcfya'
        },
        hide: !extendedDataYears,
        cellRenderer: ({ data, flag, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data, !extendedDataYears);
            if (!data.allowedFacts?.includes(41)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={(!isUPCSelected && !isBrandSelected) || !extendedDataYears || isBrandFactStacked(rowColConfigs, flag)}
                    disabledTip={disabledTip}
                    tipDelay={10}
                />
            );
        },
    }],
    }, {
        headerName: 'Share of Product - Base Periods',
        groupId: 'productSelectionsBasePeriods',
        marryChildren: true,
        children: [{
        colId: 'productSelectionsCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'CY',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'ps'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(9)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={
                        isFactDisabled(data) ||
                        isProductSelectionsFactStacked(rowColConfigs, flag) ||
                        isBrandFactStacked(rowColConfigs, flag) ||
                        isCategoryFactStacked(rowColConfigs, flag) ||
                        isSubCategoryFactStacked(rowColConfigs, flag)
                    }
                    disabledTip={disabledTip}
                />
            );
        },
    }, {
        colId: 'productSelectionsYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'psya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(10)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={
                        isFactDisabled(data) ||
                        isProductSelectionsFactStacked(rowColConfigs, flag) ||
                        isBrandFactStacked(rowColConfigs, flag) ||
                        isCategoryFactStacked(rowColConfigs, flag) ||
                        isSubCategoryFactStacked(rowColConfigs, flag)
                    }
                    disabledTip={disabledTip}
                />
            );
        },
    },
    {
        colId: 'productSelectionsTwoYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: '2 YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'pstya'
        },
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(28)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={
                        isFactDisabled(data) ||
                        isProductSelectionsFactStacked(rowColConfigs, flag) ||
                        isBrandFactStacked(rowColConfigs, flag) ||
                        isCategoryFactStacked(rowColConfigs, flag) ||
                        isSubCategoryFactStacked(rowColConfigs, flag)
                    }
                    disabledTip={disabledTip}
                />
            );
        },
    },
    {
        colId: 'productSelectionsThreeYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: '3 YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'pstya3'
        },
        hide: !extendedDataYears,
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data, !extendedDataYears);
            if (!data.allowedFacts?.includes(29)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={
                        isFactDisabled(data) ||
                        !extendedDataYears ||
                        isProductSelectionsFactStacked(rowColConfigs, flag) ||
                        isBrandFactStacked(rowColConfigs, flag) ||
                        isCategoryFactStacked(rowColConfigs, flag) ||
                        isSubCategoryFactStacked(rowColConfigs, flag)
                    }
                    disabledTip={disabledTip}
                    tipDelay={10}
                />
            );
        },
    },
    {
        colId: 'productSelectionsFourYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: '4 YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'psfya'
        },
        hide: !extendedDataYears,
        cellRenderer: ({ data, flag, rowIndex, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data, !extendedDataYears);
            if (!data.allowedFacts?.includes(30)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={
                        isFactDisabled(data) ||
                        !extendedDataYears ||
                        isProductSelectionsFactStacked(rowColConfigs, flag) ||
                        isBrandFactStacked(rowColConfigs, flag) ||
                        isCategoryFactStacked(rowColConfigs, flag) ||
                        isSubCategoryFactStacked(rowColConfigs, flag)
                    }
                    disabledTip={disabledTip}
                    tipDelay={10}
                />
            );
        }
    }],
    }, {
        headerName: 'Share of Product - Share Point Change',
        groupId: 'productSelectionsSharePointChange',
        marryChildren: true,
        children: [{
        colId: 'productSelectionsSharePointChangeYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'SPC vs. YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'psspcya'
        },
        cellRenderer: ({ data, flag, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(42)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={
                        isFactDisabled(data) ||
                        isProductSelectionsFactStacked(rowColConfigs, flag) ||
                        isBrandFactStacked(rowColConfigs, flag) ||
                        isCategoryFactStacked(rowColConfigs, flag) ||
                        isSubCategoryFactStacked(rowColConfigs, flag)
                    }
                    disabledTip={disabledTip}
                />
            );
        },
    },
    {
        colId: 'productSelectionsSharePointChangeTwoYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'SPC vs. 2 YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'psspctya'
        },
        cellRenderer: ({ data, flag, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data);
            if (!data.allowedFacts?.includes(43)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={
                        isFactDisabled(data) ||
                        isProductSelectionsFactStacked(rowColConfigs, flag) ||
                        isBrandFactStacked(rowColConfigs, flag) ||
                        isCategoryFactStacked(rowColConfigs, flag) ||
                        isSubCategoryFactStacked(rowColConfigs, flag)
                    }
                    disabledTip={disabledTip}
                />
            );
        },
    },
    {
        colId: 'productSelectionsSharePointChangeThreeYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'SPC vs. 3 YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'psspctya3'
        },
        hide: !extendedDataYears,
        cellRenderer: ({ data, flag, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data, !extendedDataYears);
            if (!data.allowedFacts?.includes(44)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={
                        isFactDisabled(data) ||
                        !extendedDataYears ||
                        isProductSelectionsFactStacked(rowColConfigs, flag) ||
                        isBrandFactStacked(rowColConfigs, flag) ||
                        isCategoryFactStacked(rowColConfigs, flag) ||
                        isSubCategoryFactStacked(rowColConfigs, flag)
                    }
                    disabledTip={disabledTip}
                    tipDelay={10}
                />
            );
        },
    },
    {
        colId: 'productSelectionsSharePointChangeFourYearAgoCheckbox',
        minWidth: checkboxColumnWidth,
        flex: 1,
        headerClass: `${baseClassName}__checkbox-header`,
        headerName: 'SPC vs. 4 YA',
        cellClass: `${baseClassName}__checkbox-cell`,
        cellRendererParams: {
            flag: 'psspcfya'
        },
        hide: !extendedDataYears,
        cellRenderer: ({ data, flag, context }) => {
            const value = `${data.id}:${flag}`;
            const stackedFieldTip = factToolTipForStackedField(rowColConfigs, flag);
            const disabledTip = stackedFieldTip !== '' ? stackedFieldTip : getDisabledTip(data, !extendedDataYears);
            if (!data.allowedFacts?.includes(45)) return null;
            return (
                <ByzzerCheckbox
                    onChange={handleFactChange}
                    value={value}
                    checked={context[value]}
                    disabled={
                        isFactDisabled(data) ||
                        !extendedDataYears ||
                        isProductSelectionsFactStacked(rowColConfigs, flag) ||
                        isBrandFactStacked(rowColConfigs, flag) ||
                        isCategoryFactStacked(rowColConfigs, flag) ||
                        isSubCategoryFactStacked(rowColConfigs, flag)
                    }
                    disabledTip={disabledTip}
                    tipDelay={10}
                />
            );
        }
    }],
        }];

        return columns.map((column) => {
            if (!('children' in column)) return column;
            if (column.groupId === 'shareFactName') return column;

            const isSharePointChangeGroup = column.groupId?.includes('SharePointChange');
            const minWidth = isSharePointChangeGroup
                ? sharePointChangeColumnWidth
                : shareBasePeriodColumnWidth;
            const sharePointChangeColumnIds = new Set<string>(column.children.map(
                (child) => String((child as ByzColDef<DodFactSet, DodFactValuePickerTableColId>).colId)
            ));
            const isFirstDisplayedSharePointChangeColumn = (params: HeaderClassParams | CellClassParams) => {
                const firstDisplayedColumnId = params.columnApi.getAllDisplayedColumns()
                    .find((displayedColumn) => sharePointChangeColumnIds.has(displayedColumn.getColId()))
                    ?.getColId();

                return isSharePointChangeGroup && firstDisplayedColumnId === params.column?.getColId();
            };

            return {
                ...column,
                headerClass: classNames(column.headerClass, {
                    [`${baseClassName}__group-divider`]: isSharePointChangeGroup,
                }),
                headerTooltip: column.headerName,
                suppressStickyLabel: true,
                children: column.children.map((child) => {
                    const leafColumn = child as ByzColDef<DodFactSet, DodFactValuePickerTableColId>;

                    return {
                        ...leafColumn,
                        headerClass: (params: HeaderClassParams) => classNames(leafColumn.headerClass, {
                            [`${baseClassName}__column-divider`]: isFirstDisplayedSharePointChangeColumn(params),
                        }),
                        cellClass: (params: CellClassParams) => classNames(leafColumn.cellClass, {
                            [`${baseClassName}__column-divider`]: isFirstDisplayedSharePointChangeColumn(params),
                        }),
                        flex: isSharePointChangeGroup ? 1.4 : 1,
                        width: minWidth,
                        minWidth,
                    };
                }),
            };
        });
    }, []);

    const visibleShareFactsColumnDefs = useMemo(() => {
        const visibleGroupIds = new Set(shareGroupIds[shareFactGroup]);

        return shareFactsColumnDefs.filter((column) => (
            !('children' in column) || (column.groupId && visibleGroupIds.has(column.groupId))
        ));
    }, [shareFactGroup, shareFactsColumnDefs]);

    function getEligibleVisibleShareSelectionKeys(): string[] {
        const api = gridRef.current?.api;
        const columnApi = gridRef.current?.columnApi;
        if (!api || !columnApi) return [];

        const visibleColumnIds = new Set(
            columnApi.getColumnState()
                .filter(({ hide }) => !hide)
                .map(({ colId }) => colId)
        );
        const visibleColumns = getFactLeafColumnDefs(shareFactsColumnDefs)
            .filter((column) => {
                const flag = column.cellRendererParams?.flag;
                return flag && column.colId && visibleColumnIds.has(column.colId as string);
            });
        const keys: string[] = [];

        for (let rowIndex = 0; rowIndex < api.getDisplayedRowCount(); rowIndex++) {
            const fact = api.getDisplayedRowAtIndex(rowIndex)?.data as DodFactSet | undefined;
            if (!fact) continue;

            for (const column of visibleColumns) {
                const flag = column.cellRendererParams?.flag as string;
                if (!fact.allowedFacts?.includes(flagToAllowedIndexMapping[flag])) continue;

                const cell = typeof column.cellRenderer === 'function'
                    ? column.cellRenderer({ data: fact, flag, rowIndex, context: selectionsRef.current } as any)
                    : null;
                const isDisabled = React.isValidElement<{ disabled?: boolean }>(cell)
                    && Boolean(cell.props.disabled);

                if (!isDisabled) keys.push(`${fact.id}:${flag}`);
            }
        }

        return keys;
    }

    function isSelectAllChecked(): boolean {
        const keys = getEligibleVisibleShareSelectionKeys();
        return keys.length > 0 && keys.every((key) => selectionsRef.current[key]);
    }

    function handleSelectAllChange(checked: boolean): void {
        const keys = getEligibleVisibleShareSelectionKeys();
        if (!keys.length) return;

        if (checked) {
            const scope = new Set(keys);
            const selectedOutsideScope = Object.entries(selectionsRef.current)
                .filter(([key, selected]) => selected && !scope.has(key))
                .length;

            if (selectedOutsideScope + keys.length > limit) {
                alertSelectionLimitExceed('facts', limit);
                return;
            }
        }

        lastFlagAndIndex.current = null;
        shiftActive.current = false;
        shiftBaselineRef.current = null;
        lastAppliedKeysRef.current = new Set();
        setSelections((current) => {
            const next = { ...current };
            keys.forEach((key) => {
                if (checked) {
                    next[key] = true;
                } else {
                    delete next[key];
                }
            });
            return next;
        });
        queueFullRedraw();
    }

    const handleGridReady = useCallback(({ api, columnApi }: GridReadyEvent) => {
        if (filterType.data.type === 'core') return;

        const savedVisibility = shareColumnVisibilityRef.current[shareFactGroup];
        columnApi.applyColumnState({
            state: getFactLeafColumnDefs(visibleShareFactsColumnDefs)
                .filter((column) => column.colId !== 'shareDisplay')
                .map((column) => ({
                    colId: column.colId as string,
                    width: column.width,
                    ...(savedVisibility?.[column.colId as string] !== undefined && {
                        hide: !savedVisibility[column.colId as string],
                    }),
                })),
        });
    }, [filterType.data.type, shareFactGroup, visibleShareFactsColumnDefs]);

    const handleColumnVisibilityChange = useCallback((colId: string, visible: boolean) => {
        if (filterType.data.type === 'core') return;

        shareColumnVisibilityRef.current[shareFactGroup] = {
            ...shareColumnVisibilityRef.current[shareFactGroup],
            [colId]: visible,
        };
        gridRef.current?.api.refreshHeader();
        gridRef.current?.api.refreshCells({ force: true });
    }, [filterType.data.type, shareFactGroup]);

    useEffect(() => {
        setSelections(() => {


            return value.reduce((selections, factSet) => {

                const {
                    id,
                    fact: currentPeriod,
                    yearAgo,
                    changeVsYearAgo,
                    percentChangeVsYearAgo,
                    twoYearAgo,
                    threeYearAgo,
                    fourYearAgo,
                    category,
                    categoryYearAgo,
                    categoryTwoYearAgo,
                    categoryThreeYearAgo,
                    categoryFourYearAgo,
                    categorySharePointChangeYearAgo,
                    categorySharePointChangeTwoYearAgo,
                    categorySharePointChangeThreeYearAgo,
                    categorySharePointChangeFourYearAgo,
                    brand,
                    brandYearAgo,
                    brandTwoYearAgo,
                    brandThreeYearAgo,
                    brandFourYearAgo,
                    brandSharePointChangeYearAgo,
                    brandSharePointChangeTwoYearAgo,
                    brandSharePointChangeThreeYearAgo,
                    brandSharePointChangeFourYearAgo,
                    productSelections,
                    productSelectionsYearAgo,
                    productSelectionsTwoYearAgo,
                    productSelectionsThreeYearAgo,
                    productSelectionsFourYearAgo,
                    productSelectionsSharePointChangeYearAgo,
                    productSelectionsSharePointChangeTwoYearAgo,
                    productSelectionsSharePointChangeThreeYearAgo,
                    productSelectionsSharePointChangeFourYearAgo,
                    subCategory,
                    subCategoryYearAgo,
                    subCategoryTwoYearAgo,
                    subCategoryThreeYearAgo,
                    subCategoryFourYearAgo,
                    subCategorySharePointChangeYearAgo,
                    subCategorySharePointChangeTwoYearAgo,
                    subCategorySharePointChangeThreeYearAgo,
                    subCategorySharePointChangeFourYearAgo,
                    changeVsTwoYearAgo,
                    changeVsThreeYearAgo,
                    changeVsFourYearAgo,
                    percentChangeVsTwoYearAgo,
                    percentChangeVsThreeYearAgo,
                    percentChangeVsFourYearAgo
                } = factSet;
                selections[`${id}:cp`] = Boolean(currentPeriod);
                selections[`${id}:ya`] = Boolean(yearAgo);
                selections[`${id}:cvya`] = Boolean(changeVsYearAgo);
                selections[`${id}:pcvya`] = Boolean(percentChangeVsYearAgo);
                selections[`${id}:tya`] = Boolean(twoYearAgo);
                selections[`${id}:cvtya`] = Boolean(changeVsTwoYearAgo);
                selections[`${id}:pcvtya`] = Boolean(percentChangeVsTwoYearAgo);
                selections[`${id}:tya3`] = Boolean(threeYearAgo);
                selections[`${id}:cvtya3`] = Boolean(changeVsThreeYearAgo);
                selections[`${id}:pcvtya3`] = Boolean(percentChangeVsThreeYearAgo);
                selections[`${id}:fya`] = Boolean(fourYearAgo);
                selections[`${id}:cvfya`] = Boolean(changeVsFourYearAgo);
                selections[`${id}:pcvfya`] = Boolean(percentChangeVsFourYearAgo);
                selections[`${id}:c`] = Boolean(category);
                selections[`${id}:cya`] = Boolean(categoryYearAgo);
                selections[`${id}:ctya`] = Boolean(categoryTwoYearAgo);
                selections[`${id}:ctya3`] = Boolean(categoryThreeYearAgo);
                selections[`${id}:cfya`] = Boolean(categoryFourYearAgo);
                selections[`${id}:cspcya`] = Boolean(categorySharePointChangeYearAgo);
                selections[`${id}:cspctya`] = Boolean(categorySharePointChangeTwoYearAgo);
                selections[`${id}:cspctya3`] = Boolean(categorySharePointChangeThreeYearAgo);
                selections[`${id}:cspcfya`] = Boolean(categorySharePointChangeFourYearAgo);
                selections[`${id}:b`] = Boolean(brand);
                selections[`${id}:bya`] = Boolean(brandYearAgo);
                selections[`${id}:btya`] = Boolean(brandTwoYearAgo);
                selections[`${id}:btya3`] = Boolean(brandThreeYearAgo);
                selections[`${id}:bfya`] = Boolean(brandFourYearAgo);
                selections[`${id}:bspcya`] = Boolean(brandSharePointChangeYearAgo);
                selections[`${id}:bspctya`] = Boolean(brandSharePointChangeTwoYearAgo);
                selections[`${id}:bspctya3`] = Boolean(brandSharePointChangeThreeYearAgo);
                selections[`${id}:bspcfya`] = Boolean(brandSharePointChangeFourYearAgo);
                selections[`${id}:ps`] = Boolean(productSelections);
                selections[`${id}:psya`] = Boolean(productSelectionsYearAgo);
                selections[`${id}:pstya`] = Boolean(productSelectionsTwoYearAgo);
                selections[`${id}:pstya3`] = Boolean(productSelectionsThreeYearAgo);
                selections[`${id}:psfya`] = Boolean(productSelectionsFourYearAgo);
                selections[`${id}:psspcya`] = Boolean(productSelectionsSharePointChangeYearAgo);
                selections[`${id}:psspctya`] = Boolean(productSelectionsSharePointChangeTwoYearAgo);
                selections[`${id}:psspctya3`] = Boolean(productSelectionsSharePointChangeThreeYearAgo);
                selections[`${id}:psspcfya`] = Boolean(productSelectionsSharePointChangeFourYearAgo);
                selections[`${id}:sc`] = Boolean(subCategory);
                selections[`${id}:scya`] = Boolean(subCategoryYearAgo);
                selections[`${id}:sctya`] = Boolean(subCategoryTwoYearAgo);
                selections[`${id}:sctya3`] = Boolean(subCategoryThreeYearAgo);
                selections[`${id}:scfya`] = Boolean(subCategoryFourYearAgo);
                selections[`${id}:scspcya`] = Boolean(subCategorySharePointChangeYearAgo);
                selections[`${id}:scspctya`] = Boolean(subCategorySharePointChangeTwoYearAgo);
                selections[`${id}:scspctya3`] = Boolean(subCategorySharePointChangeThreeYearAgo);
                selections[`${id}:scspcfya`] = Boolean(subCategorySharePointChangeFourYearAgo);
                return selections;
            }, {} as Record<string, boolean>)
        })
        queueFullRedraw();
    }, [value]);

    useEffect(() => {
        const facts = factConfig?.[filterType.data.type]
            ?.find((val) => val.value === filterType.type)
            ?.factLists ?? [];
        setCurrentFacts(facts);
        setMatchingFacts(facts);
    }, [filterType, factConfig]);

    useEffect(() => {
        filterFacts(filterText);
    }, [filterText]);

    useEffect(() => {
        gridRef.current?.api?.refreshHeader();
    }, [selections, matchingFacts]);

    const filterFacts = useCallback(debounce(async (filterText: string) => {
        if (filterText) {
            const matcher = getRegexForSearchString(filterText);
            setMatchingFacts(currentFactsRef.current.filter(({ display }) => matcher.test(display)));
        } else {
            setMatchingFacts(currentFactsRef.current);
        }
    }, 500), []);

    function handleCheckboxClick(e: MouseEvent, index: number, flag: string): void {
        // This function is responsible for handle shift select
        if (!(e.target as HTMLElement).matches('input[type="checkbox"]')) return;

        // If shift was previously active but is no longer pressed, reset shift state
        if (shiftActive.current && !e.shiftKey) {
            shiftActive.current = false;
        }

        // Block shift-to-select for free users
        if (e.shiftKey && isFreeUser) {
            // Idea - We could show a tooltip or message about upgrading to access this feature
            return;
        }

        if (e.shiftKey && lastFlagAndIndex.current) {
            // Important: preventDefault to stop native checkbox behavior
            e.preventDefault();

            const startRow = Math.min(lastFlagAndIndex.current.index, index);
            const endRow = Math.max(lastFlagAndIndex.current.index, index);

            const allColDefs = filterType.data.type === 'core' ? coreFactsColumnDefs : getFactLeafColumnDefs(visibleShareFactsColumnDefs);
            const allFlags = allColDefs
                .map(col => col.cellRendererParams?.flag)
                .filter(Boolean) as string[];

            const startColIndex = allFlags.indexOf(lastFlagAndIndex.current.flag);
            const endColIndex = allFlags.indexOf(flag);

            if (startColIndex === -1 || endColIndex === -1) {
                return;
            }

            const startCol = Math.min(startColIndex, endColIndex);
            const endCol = Math.max(startColIndex, endColIndex);

            // Use baseline snapshot (if available) so we don't destructively remove unrelated selections.
            const baseline = shiftBaselineRef.current ? { ...shiftBaselineRef.current } : cloneDeep(selections);

            // Anchor state is read from the baseline so repeated shift-reshapes are deterministic
            const anchorKey = `${matchingFactsRef.current[lastFlagAndIndex.current.index].id}:${lastFlagAndIndex.current.flag}`;
            const anchorState = baseline[anchorKey];
            const isChecking = Boolean(anchorState);

            // Start from baseline and revert any previous shift-applied keys that are now outside the new rectangle
            const newSelections = { ...baseline };

            // Get keys that were applied by the previous shift operation
            const prevAppliedKeys = lastAppliedKeysRef.current;

            // Build set of keys that will be applied by the new rectangle
            const newAppliedKeys = new Set<string>();
            const rowsToRedrawSet = new Set<any>();

            for (let i = startRow; i <= endRow; i++) {
                const rowNode = gridRef.current?.api?.getDisplayedRowAtIndex(i);
                if (!rowNode?.data) continue;
                rowsToRedrawSet.add(rowNode);

                const fact = rowNode.data as DodFactSet;
                for (let j = startCol; j <= endCol; j++) {
                    const currentFlag = allFlags[j];
                    const valueKey = `${fact.id}:${currentFlag}`;

                    const isAllowed = fact.allowedFacts?.includes(flagToAllowedIndexMapping[currentFlag]);

                    // Get the column definition for this flag
                    const colDef = allColDefs.find(col => col.cellRendererParams?.flag === currentFlag);
                    let isDisabled = false;

                    if (colDef?.cellRenderer && typeof colDef.cellRenderer === 'function') {
                        // Simulate the exact same parameters that the cell renderer receives.
                        const rendererParams = {
                            data: fact,
                            flag: currentFlag,
                            rowIndex: i,
                            context: selections
                        };

                        // Call the cell renderer to get the actual component
                        // @ts-expect-error - rendererParams is a partial match for the actual params.  It works for our purposes, disabling logic.
                        const cellComponent = colDef.cellRenderer(rendererParams);

                        // Extract the disabled state from the ByzzerCheckbox props
                        if (cellComponent && typeof cellComponent === 'object' && 'props' in cellComponent) {
                            isDisabled = Boolean(cellComponent.props?.disabled);
                        }
                    }

                    if (isAllowed && !isDisabled) {
                        newAppliedKeys.add(valueKey);
                    }
                }
            }

            // Revert any previous shift-applied keys that are not in the new rectangle
            // AND add their rows to the redraw set so the UI updates to show deselection
            for (const prevKey of prevAppliedKeys) {
                if (!newAppliedKeys.has(prevKey)) {
                    // Find which row this key belongs to and mark it for redraw
                    const [factId] = prevKey.split(':');
                    const factIndex = matchingFactsRef.current.findIndex(fact => fact.id === Number(factId));
                    if (factIndex !== -1) {
                        const rowNode = gridRef.current?.api?.getDisplayedRowAtIndex(factIndex);
                        if (rowNode?.data) {
                            rowsToRedrawSet.add(rowNode);
                        }
                    }

                    // Revert to baseline value (might be undefined, which means delete)
                    if (baseline.hasOwnProperty(prevKey)) {
                        newSelections[prevKey] = baseline[prevKey];
                    } else {
                        delete newSelections[prevKey];
                    }
                }
            }

            // Apply the new rectangle keys
            for (const key of newAppliedKeys) {
                newSelections[key] = isChecking;
            }

            // Update tracking of which keys were applied by this shift operation
            lastAppliedKeysRef.current = newAppliedKeys;

            // Apply the new selection state (functional update to avoid stale closures)
            setSelections(() => newSelections);
            // Queue affected rows for redraw
            const rowNodesToRedraw = Array.from(rowsToRedrawSet);
            if (rowNodesToRedraw.length) {
                queueRowRedraw(rowNodesToRedraw);
            }

            // Keep shift mode active for continuous shift-selections
            shiftActive.current = true;
        } else {
            // For a normal click, update the anchor point
            lastFlagAndIndex.current = { index, flag };
            shiftActive.current = false;
            // snapshot current selections as baseline for subsequent shift operations
            shiftBaselineRef.current = cloneDeep(selections);
            // clear tracking when setting new anchor
            lastAppliedKeysRef.current = new Set();
            // Let the normal checkbox change handler run
        }
    }

    function handleFactChange({ value, checked }: ByzzerCheckableChangeEvent<string>) {
        if (shiftActive.current) {
            shiftActive.current = false;
            return;
        }
        setSelections(selections => {
            const isSelectionLimitExceed = Object.keys(selections).filter((fact) => selections[fact] === true).length >= limit
            if (checked && isSelectionLimitExceed) {
                alertSelectionLimitExceed('facts', limit)
            }
            return ({
                ...selections,
                ...(!isSelectionLimitExceed || !checked) && { [value]: checked }
            })
        });
        // Queue redraw for the specific row that changed
        const [factId] = value.split(':');
        const factIndex = matchingFactsRef.current.findIndex(fact => fact.id === Number(factId));
        if (factIndex !== -1) {
            const rowNode = gridRef.current?.api?.getDisplayedRowAtIndex(factIndex);
            if (rowNode?.data) {
                queueRowRedraw([rowNode]);
            } else {
                queueFullRedraw();
            }
        } else {
            queueFullRedraw();
        }
    }

    function handleClearClick() {
        lastFlagAndIndex.current = null;
        shiftActive.current = false;
        shiftBaselineRef.current = null;
        lastAppliedKeysRef.current = new Set();
        // only remove the
        const flags = filterType.data.type === 'core' ? coreFlags : shareFlagsByGroup[shareFactGroup];
        const factIds: number[] = currentFactsRef.current.map(fact => fact.id);

        setSelections(selections => {
            const newSelections = { ...selections };

            for (let flag of flags) {
                for (let id of factIds) {
                    delete newSelections[`${id}:${flag}`];
                }
            }

            return newSelections;
        });

        // Clear affects all rows, so queue full redraw
        queueFullRedraw();
    }

    const flushRedrawQueue = useCallback(debounce(() => {
        try {
            if (hasPendingFullRedraw.current) {
                // Full redraw takes precedence
                gridRef.current?.api?.redrawRows();
                hasPendingFullRedraw.current = false;
                pendingRowsToRedraw.current.clear();
            } else if (pendingRowsToRedraw.current.size > 0) {
                // Partial redraw of specific rows
                const rowNodes = Array.from(pendingRowsToRedraw.current);
                gridRef.current?.api?.redrawRows({ rowNodes });
                pendingRowsToRedraw.current.clear();
            }
        } catch {
            // Fallback to full redraw on error
            gridRef.current?.api?.redrawRows();
            hasPendingFullRedraw.current = false;
            pendingRowsToRedraw.current.clear();
        }
    }, 50), []);

    // Batched redraw functions
    const queueRowRedraw = useCallback((rowNodes: any[]) => {
        rowNodes.forEach(node => pendingRowsToRedraw.current.add(node));
        flushRedrawQueue();
    }, [flushRedrawQueue]);

    const queueFullRedraw = useCallback(() => {
        hasPendingFullRedraw.current = true;
        flushRedrawQueue();
    }, [flushRedrawQueue]);

    function handleApplyClick() {
        // the first entry in core facts contains every possible fact, all other entries are just subsets
        const allFacts = factConfig.core.find(v => v.value === "All Facts").factLists;
        const facts = Object.entries(selections).filter(([, value]) => value).reduce<DodFactSet[]>((facts, [value]) => {
            if (!value) return facts;
            const [id, type] = value.split(':');
            // attempt to find the fact in the current selections
            let fact = facts.find(fact => fact.id === Number(id));
            if (!fact) {
                // if it doesn't exist create it by cloning the value from the filters
                fact = cloneDeep(allFacts.find(fact => fact.id === Number(id)));
                facts.push(fact!);
            }
            fact![flagToFactMapping[type]] = true;
            return facts;
        }, []);
        onApply?.({
            name,
            value: facts,
        });
    }

    const handleCellClicked = (event: CellClickedEvent<DodFactSet>) => {
        const { rowIndex, colDef, event: browserEvent } = event;
        const flag = (colDef as ColDef).cellRendererParams?.flag;
        const mouseEvent = browserEvent as MouseEvent;

        if (flag && mouseEvent && rowIndex !== null && (mouseEvent.target as HTMLElement).matches('input[type="checkbox"]')) {
            handleCheckboxClick(mouseEvent, rowIndex, flag);
        }
    };

    return (
        <div className={classNames(baseClassName, className)} {...props}>
            <div className={`${baseClassName}__options`}>
                <ByzzerTable ref={gridRef}
                    gridOptions={checkboxGridOptions}
                    context={selections}
                    key={`${filterType?.type}-${shareFactGroup}`}
                    rowData={matchingFacts}
                    suppressRowHoverHighlight
                    suppressCellFocus
                    suppressMovableColumns
                    columnDefs={filterType.data.type === 'core' ? coreFactsColumnDefs : visibleShareFactsColumnDefs}
                    tableArea={filterType.data.type === 'core' ? 'dodCoreFactValuePicker' : 'dodShareFactValuePicker'}
                    defaultColDef={defaultColDef}
                    enableSaveLayout={true}
                    enableSaveSort={false} // BYZ-15636 better practice to pass value for enableSaveSort along with enableSaveLayout prop
                    factTriggerRef={factTriggerRef}
                    uncheckedColumns={extendedDataYears ? [] : ['fourYearAgoCheckbox', 'threeYearAgoCheckbox']}
                    onCellClicked={handleCellClicked}
                    onGridReady={handleGridReady}
                    onColumnVisibilityChange={handleColumnVisibilityChange}
                />
            </div>

            <div className={`${baseClassName}__actions`}>
                {actions?.map((config, index) => (
                    <ByzzerButton
                        key={index}
                        type={config.type}
                        className={config.className}
                        iconType={config.icon}
                        tip={config.tip}
                        tipDelay={[500, 0]}
                        label={config.label}
                        onClick={() => config.action()}
                    />
                ))}
                <div className={`${baseClassName}__actions-divider`} />
                <ByzzerButton type={'negative'} onClick={handleClearClick}>
                    Clear
                </ByzzerButton>
                <ByzzerButton onClick={handleApplyClick}>Apply</ByzzerButton>
            </div>
        </div>
    );
};

export default DodFactValuePickerTable;
